import { createCommerceAxiosClient } from '@lib/http/commerceAxiosClient';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import {
  readTabActiveOrgCookie,
  subscribeToActiveOrgCookie,
} from '@lib/util/activeOrgCookie';
import { resolveActiveOrg } from '@lib/util/resolveActiveOrg';
import * as Sentry from '@sentry/nextjs';
import type { AxiosInstance } from 'axios';
import { useSession } from 'next-auth/react';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import useSWR, { unstable_serialize } from 'swr';
import {
  subscribeToCartChanged,
  subscribeToOtherTabCartChanged,
} from './cartChanged';
import {
  CART_COUNT_STALE_TIME_MS,
  type CartLine,
  areCartCountReadsHeld,
  cartCountRetryDelay,
  fetchCartLines,
  fetchGuestCartLines,
  holdCartCountReads,
  retryAfterMs,
  shouldReportCartCountError,
  shouldRetryCartCount,
  subscribeToCheckoutReturn,
  subscribeToPageRestore,
} from './cartCount';
import { hasUsableBridgeToken } from './ensureBridgeToken';
import { readGuestCartId, subscribeToGuestCart } from './guestCart';
import {
  cartLinesOwner,
  isCartReadHosted,
  readSharedCartLines,
  subscribeToSharedCartLines,
} from './sharedCartLines';
import {
  useBridgeTokenProvisioning,
  useEnsureBridgeToken,
} from './useBridgeToken';

let client: AxiosInstance | undefined;

/** One bridge client for every header on the page; exported for stories to stub. */
export const getCartCountClient = (): AxiosInstance =>
  (client ??= createCommerceAxiosClient('usg-direct'));

type CartCountKey = readonly [
  'commerce-cart-count',
  userId: string,
  organizationId: string,
];

type GuestCartCountKey = readonly ['commerce-guest-cart-count', cartId: string];

const fetchLines = async (
  [, userId, organizationId]: CartCountKey,
  ensureBridgeToken: () => Promise<string | undefined>
) => {
  if (!(await ensureBridgeToken()) || !hasUsableBridgeToken(userId)) {
    return undefined;
  }
  // The header mini-cart may have mounted while the token was ensured.
  if (isCartReadHosted()) return undefined;
  return fetchCartLines(getCartCountClient(), { userId, organizationId });
};

const fetchGuestLines = async (
  [, cartId]: GuestCartCountKey,
  ensureBridgeToken: () => Promise<string | undefined>
) => {
  if (!(await ensureBridgeToken())) {
    return undefined;
  }
  // Provisioning a new guest identity forgets the lapsed one's cart.
  if (readGuestCartId() !== cartId) {
    return [];
  }
  if (isCartReadHosted()) return undefined;
  return fetchGuestCartLines(getCartCountClient(), cartId);
};

const readers = new Set<() => void>();
let unsubscribeSignals = () => {};
let isRevalidating = false;

/**
 * Every reader on the page shares one SWR entry, and a bound `mutate` always
 * starts a new request, so each signal revalidates through one reader only:
 * the header and the PDP reading the cart still cost one request per signal.
 * The first signal reads straight away, so a focus revalidation right behind
 * it is deduped; signals sent in the same synchronous run, such as writers
 * settling together, share its request, since each signals only once its
 * write has landed.
 */
const revalidateOnce = () => {
  if (isRevalidating) return;
  isRevalidating = true;
  queueMicrotask(() => {
    isRevalidating = false;
  });
  const [first] = readers;
  first();
};

const subscribeReaderToSignals = (revalidate: () => void) => {
  if (readers.size === 0) {
    const unsubscribers = [
      subscribeToCartChanged(revalidateOnce),
      // The badge and the PDP agree with a change made in another tab once
      // this one is shown, not a stale time later.
      subscribeToOtherTabCartChanged(revalidateOnce),
      subscribeToPageRestore(revalidateOnce),
      subscribeToCheckoutReturn(revalidateOnce),
    ];
    unsubscribeSignals = () =>
      unsubscribers.forEach((unsubscribe) => unsubscribe());
  }
  readers.add(revalidate);
  return () => {
    readers.delete(revalidate);
    if (readers.size === 0) unsubscribeSignals();
  };
};

export type CartLines = {
  /**
   * The active cart's lines; `undefined` while unknown — still resolving,
   * rate limited, failed or disabled — so no reader mistakes it for an empty
   * cart.
   */
  lines: CartLine[] | undefined;
};

/**
 * Read-only cart lines for the multi-product cart: the header count
 * (CPS-7694) and the PDP's in-cart state (CPS-7695) share this one read. Never
 * creates a cart (see `buildCartCountPath`), caches for
 * `CART_COUNT_STALE_TIME_MS`, and revalidates on the cart-changed signal, from
 * this tab or another, and when returning from checkout, including a tab left
 * mounted.
 *
 * Guests read the guest cart commerce-core keeps for them (CPS-7693), with a
 * guest token; a guest without one reads no lines, with no request and
 * nothing minted.
 *
 * Under the header mini-cart, which reads the cart on every page, this
 * requests nothing and serves the lines that read published (CPS-7696), so a
 * hard PDP load reads the cart once.
 *
 * `enabled: false` reads nothing, for callers that render with the
 * MULTI_CART flag off.
 */
export const useCartLines = ({
  enabled = true,
  organizationId: renderedOrganizationId,
}: {
  enabled?: boolean;
  /** The org a page was rendered for; before it is followed the tab's is stale. */
  organizationId?: string;
} = {}): CartLines => {
  const { data: session, status } = useSession();
  const { isReady: isBridgeTokenReady } = useBridgeTokenProvisioning({
    enabled,
  });
  const ensureBridgeToken = useEnsureBridgeToken();
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;
  const isGuest = status === 'unauthenticated';
  // Unknown on the server, so the badge hydrates as it was rendered there.
  const guestCartId = useSyncExternalStore(
    subscribeToGuestCart,
    readGuestCartId,
    () => undefined
  );
  // Client snapshots on hydration too: the header has marked its read by the
  // time the page renders, and nothing it renders depends on these.
  const isHosted = useSyncExternalStore(
    subscribeToSharedCartLines,
    isCartReadHosted,
    isCartReadHosted
  );
  const shared = useSyncExternalStore(
    subscribeToSharedCartLines,
    readSharedCartLines,
    readSharedCartLines
  );

  // Same key as the mini-cart island, so one request serves both.
  const { data: orgsData } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId?: string;
  }>(enabled && userId ? ['/api/users/organizations', userId] : null, ([url]) =>
    failingFetcher(url)
  );

  // The header sits outside OrganizationsProvider, so it reads the org this
  // tab works in: a switch made here, or a PDP rendered for another org,
  // re-keys the read to that org's cart; a switch made in another tab alone
  // does not (see readTabActiveOrgCookie).
  const activeOrgCookie = useSyncExternalStore(
    subscribeToActiveOrgCookie,
    readTabActiveOrgCookie,
    () => undefined
  );
  const organizationId =
    renderedOrganizationId ??
    resolveActiveOrg(
      activeOrgCookie,
      orgsData?.organizations,
      orgsData?.defaultOrganizationId
    )?.id;
  const cancelHiddenRetryRef = useRef<() => void>(() => {});
  const readyUserIdRef = useRef<string>();
  const reportedErrorKeyRef = useRef<string>();
  useEffect(() => () => cancelHiddenRetryRef.current(), []);
  useEffect(() => {
    if (isBridgeTokenReady) readyUserIdRef.current = userId;
  }, [isBridgeTokenReady, userId]);

  // Wait for initial provisioning, but keep the key after token expiry so a
  // later revalidation can renew it. The fetcher still checks the subject.
  const userKey: CartCountKey | null =
    enabled &&
    userId &&
    organizationId &&
    (isBridgeTokenReady || readyUserIdRef.current === userId)
      ? ['commerce-cart-count', userId, organizationId]
      : null;
  const guestKey: GuestCartCountKey | null =
    enabled && isGuest && guestCartId
      ? ['commerce-guest-cart-count', guestCartId]
      : null;
  const key = isHosted ? null : isGuest ? guestKey : userKey;

  const fetcher = (key: CartCountKey | GuestCartCountKey) =>
    key[0] === 'commerce-guest-cart-count'
      ? fetchGuestLines(key, ensureBridgeToken)
      : fetchLines(key, ensureBridgeToken);
  const { data, error, mutate } = useSWR(key, fetcher, {
    // Remounts and focus within the stale time are served from cache.
    dedupingInterval: CART_COUNT_STALE_TIME_MS,
    // SWRProvider turns focus revalidation off app-wide; this query wants it
    // so returning from a checkout tab refreshes a badge older than the stale
    // time.
    revalidateOnFocus: true,
    focusThrottleInterval: CART_COUNT_STALE_TIME_MS,
    // SWRProvider also turns retries off app-wide, and SWR checks this before
    // it ever calls `onErrorRetry`.
    shouldRetryOnError: true,
    onSuccess: () => {
      reportedErrorKeyRef.current = undefined;
    },
    // While held, SWR skips every revalidation of this query, focus included.
    isPaused: areCartCountReadsHeld,
    // Replaces SWR's jittered default so a 429 backs off on our schedule and
    // honours Retry-After; the badge stays hidden until a retry succeeds.
    onErrorRetry: (err, failedKey, _config, revalidate, opts) => {
      if (!shouldRetryCartCount(err, opts.retryCount)) {
        return;
      }
      const delay = cartCountRetryDelay(opts.retryCount, err);
      holdCartCountReads(Date.now() + delay);
      setTimeout(() => {
        holdCartCountReads(0);
        // SWR does not fetch a key that changed while paused, so an org
        // switch during the hold reads the new org's cart here instead.
        const latest = latestRef.current;
        if (latest.key && unstable_serialize(latest.key) !== failedKey) {
          latest.mutate();
        } else {
          revalidate(opts);
        }
      }, delay);
    },
    // Replaces SWRProvider's reporter, which would send every expected 429
    // (and each retry of it) to Sentry. Holds reads for Retry-After even
    // when SWR skips the retry for a hidden tab, so its return focus waits.
    onError: (err, failedKey) => {
      holdCartCountReads(Date.now() + retryAfterMs(err));
      // SWR skips onErrorRetry in a hidden tab. Preserve one retry through
      // the hold, then wait for visibility if the tab is still hidden.
      if (
        document.visibilityState === 'hidden' &&
        shouldRetryCartCount(err, 1)
      ) {
        const delay = cartCountRetryDelay(1, err);
        holdCartCountReads(Date.now() + delay);
        cancelHiddenRetryRef.current();
        const retryWhenVisible = () => {
          if (document.visibilityState === 'hidden') return;
          document.removeEventListener('visibilitychange', retryWhenVisible);
          cancelHiddenRetryRef.current = () => {};
          holdCartCountReads(0);
          latestRef.current.mutate();
        };
        const timer = setTimeout(() => {
          document.addEventListener('visibilitychange', retryWhenVisible);
          retryWhenVisible();
        }, delay);
        cancelHiddenRetryRef.current = () => {
          clearTimeout(timer);
          document.removeEventListener('visibilitychange', retryWhenVisible);
        };
      }
      if (
        shouldReportCartCountError(err) &&
        reportedErrorKeyRef.current !== failedKey
      ) {
        reportedErrorKeyRef.current = failedKey;
        Sentry.captureException(err);
      }
    },
  });

  const latestRef = useRef({ key, mutate });
  useEffect(() => {
    latestRef.current = { key, mutate };
  });

  useEffect(() => {
    if (!enabled || isHosted) return;
    return subscribeReaderToSignals(() => {
      mutate();
    });
  }, [enabled, isHosted, mutate]);

  if (!enabled) {
    return { lines: undefined };
  }
  if (isGuest && !guestCartId) {
    return { lines: [] };
  }
  if (isHosted) {
    const owner = cartLinesOwner({ userId, organizationId, guestCartId });
    return {
      lines: owner && shared?.owner === owner ? shared.lines : undefined,
    };
  }
  return { lines: error ? undefined : data };
};
